import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Box, Stack, Typography } from '@mui/material';

import dayjs from 'dayjs';

import { LOCATION_MONTHLY_RATE_PARAM } from '@/shared/constants';
import { useSnackbar } from '@/shared/lib/hooks/useAppSnackbar';
import { ImageResource } from '@/shared/lib/hooks/useResourceImage';
import AppButton from '@/shared/ui/AppButton';
import { ResourceImage } from '@/shared/ui/ResourceImage';
import StatusPill from '@/shared/ui/StatusPill';

import { getSearchTargetRoute } from '../model/targetRoute';
import { SearchHitEntry } from '../model/types';
import SearchOrderInsights from './SearchOrderInsights';
import { getSearchCategoryVisual } from './categoryVisuals';

const FIELD_LABEL_KEY: Record<string, string> = {
  orderNumber: 'search.fields.order_number',
  brands: 'search.fields.brands',
  published: 'search.fields.published',
  location: 'search.fields.location',
  responsible: 'search.fields.responsible',
  company: 'search.fields.company',
  organization: 'search.fields.organization',
  updatedOn: 'search.fields.updated_on',
  floor: 'search.fields.floor',
  dimensions: 'search.fields.dimensions',
  address: 'search.fields.address',
  locationType: 'search.fields.location_type',
  locationName: 'search.fields.location_name',
  dailyTraffic: 'search.fields.monthly_traffic',
  contact: 'search.fields.contact',
  companyType: 'search.fields.company_type',
  registrationNumber: 'search.fields.registration_number',
  vatId: 'search.fields.vat_id',
};

const IMAGE_KEYS = ['imageSpaceId', 'imageLocationId', 'imageDocumentId'];

interface Props {
  entry: SearchHitEntry | null;
  onOpen: (entry: SearchHitEntry) => void;
}

const SearchHitPreview: FC<Props> = ({ entry, onOpen }) => {
  const { t, i18n } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  if (!entry) {
    return (
      <Stack
        alignItems="center"
        justifyContent="center"
        gap="0.75rem"
        flex={1}
        data-testid="search-preview-empty"
      >
        <Stack
          alignItems="center"
          justifyContent="center"
          sx={{
            width: '2.75rem',
            height: '2.75rem',
            borderRadius: '0.75rem',
            backgroundColor: 'grey.50',
            color: 'grey.400',
          }}
        >
          <ManageSearchIcon />
        </Stack>
        <Typography variant="body2" color="text.secondary" textAlign="center">
          {t('search.preview.no_selection')}
        </Typography>
      </Stack>
    );
  }

  const { category, hit } = entry;
  const { Icon, heroBackground, thumbColor } = getSearchCategoryVisual(category);
  const status = hit.attributes?.status;
  const copyValue = hit.attributes?.orderNumber ?? hit.title;
  const isOrder = category === 'ORDER';
  const hasInsights = category !== 'CLIENT' && category !== 'COMPANY' && category !== 'USER';
  const insightKeys = hasInsights
    ? [
        'status',
        'totalCost',
        'budget',
        'monthlyPrice',
        'weeklyPrice',
        'startDate',
        'endDate',
        'orderNumber',
        'advSpaces',
        'advPackages',
      ]
    : ['status'];
  const thumbnailResource: ImageResource | null = !hit.attributes?.imageDocumentId
    ? category === 'USER'
      ? { kind: 'user-avatar', userId: hit.id }
      : null
    : hit.attributes.imageLocationId
      ? {
          kind: 'location-document',
          locationId: hit.attributes.imageLocationId,
          documentId: hit.attributes.imageDocumentId,
        }
      : { kind: 'thumbnail', documentId: hit.attributes.imageDocumentId };
  const fullImageResource: ImageResource | null =
    hit.attributes?.imageSpaceId && hit.attributes?.imageDocumentId
      ? {
          kind: 'space-support-picture',
          spaceId: hit.attributes.imageSpaceId,
          documentId: hit.attributes.imageDocumentId,
        }
      : null;
  const fields = Object.entries(hit.attributes ?? {}).filter(
    ([key]) => !insightKeys.includes(key) && !IMAGE_KEYS.includes(key),
  );
  const canOpen = Boolean(getSearchTargetRoute(hit.target));
  const titleText = isOrder ? (hit.subtitle ?? hit.title) : hit.title;

  const formatFieldValue = (key: string, value: string): string => {
    if (key === 'updatedOn') return dayjs(value).format('DD.MM.YYYY');
    if (key === 'dailyTraffic')
      return (Number(value) * LOCATION_MONTHLY_RATE_PARAM).toLocaleString(i18n.language);
    return value === 'true' ? t('yes') : value === 'false' ? t('no') : value;
  };

  const handleCopy = async () => {
    await navigator.clipboard.writeText(copyValue);
    enqueueSnackbar(t('search.preview.copied', { value: copyValue }), { variant: 'success' });
  };

  return (
    <Stack gap="0.75rem" flex={1} data-testid="search-preview">
      <Box
        sx={{
          padding: '0.5rem',
          borderRadius: '0.875rem',
          backgroundColor: 'background.paper',
        }}
      >
        <Box
          sx={{
            position: 'relative',
            aspectRatio: '16 / 9',
            borderRadius: '0.625rem',
            background: heroBackground,
            display: 'grid',
            placeItems: 'center',
            overflow: 'hidden',
            '& > svg': { width: '2.5rem', height: '2.5rem', color: thumbColor, opacity: 0.95 },
          }}
        >
          <Icon />
          {thumbnailResource && (
            <ResourceImage
              resource={thumbnailResource}
              alt={titleText}
              size={{ tier: 'card' }}
              width="100%"
              height="100%"
              placeholder="none"
              fallback={null}
              priority
              sx={{ position: 'absolute', inset: 0 }}
            />
          )}
          {fullImageResource && (
            <ResourceImage
              resource={fullImageResource}
              alt={titleText}
              size={{ tier: 'card' }}
              width="100%"
              height="100%"
              placeholder="none"
              fallback={null}
              sx={{ position: 'absolute', inset: 0 }}
            />
          )}
        </Box>

        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          gap="0.625rem"
          sx={{ padding: '0.75rem 0.5rem' }}
        >
          <Box minWidth={0}>
            <Typography
              sx={{ fontSize: '1rem', fontWeight: 400, lineHeight: 1.25, color: 'grey.900' }}
            >
              {titleText}
            </Typography>
            {isOrder && hit.attributes?.orderNumber ? (
              <Typography
                sx={{ fontSize: '0.625rem', fontWeight: 350, lineHeight: 1.25, color: 'grey.900' }}
              >
                #{hit.attributes.orderNumber}
              </Typography>
            ) : (
              !isOrder &&
              hit.subtitle && (
                <Typography
                  sx={{
                    fontSize: '0.625rem',
                    fontWeight: 350,
                    lineHeight: 1.25,
                    color: 'grey.900',
                  }}
                >
                  {hit.subtitle}
                </Typography>
              )
            )}
          </Box>
          {status && (
            <Box flexShrink={0}>
              <StatusPill status={status} />
            </Box>
          )}
        </Stack>
      </Box>

      {hasInsights && hit.attributes && <SearchOrderInsights attributes={hit.attributes} />}

      {fields.length > 0 && (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'auto 1fr',
            alignItems: 'baseline',
            gap: '0.5625rem 0.875rem',
            padding: '0.875rem',
            borderRadius: '0.75rem',
            backgroundColor: 'background.paper',
          }}
        >
          {fields.map(([key, value]) => (
            <Box key={key} sx={{ display: 'contents' }}>
              <Typography
                sx={{ fontSize: '0.875rem', fontWeight: 400, lineHeight: 1.25, color: 'grey.900' }}
              >
                {t(FIELD_LABEL_KEY[key] ?? key)}
              </Typography>
              <Typography
                sx={{
                  fontSize: '0.75rem',
                  fontWeight: 400,
                  lineHeight: 1.25,
                  color: 'grey.900',
                  overflowWrap: 'anywhere',
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {formatFieldValue(key, value)}
              </Typography>
            </Box>
          ))}
        </Box>
      )}

      <Stack direction="row" gap="0.5rem" marginTop="auto">
        <AppButton
          variant="primary"
          size="medium"
          fullWidth
          disabled={!canOpen}
          startIcon={<OpenInNewIcon />}
          onClick={() => onOpen(entry)}
          data-testid="search-preview-open"
        >
          {t('search.preview.open')}
        </AppButton>
        <AppButton
          variant="secondary"
          size="medium"
          fullWidth
          startIcon={<ContentCopyIcon />}
          onClick={handleCopy}
          data-testid="search-preview-copy"
        >
          {hit.attributes?.orderNumber
            ? t('search.preview.copy_number')
            : t('search.preview.copy_title')}
        </AppButton>
      </Stack>
    </Stack>
  );
};

export default SearchHitPreview;
